import React, { useCallback, useState } from 'react';
import {
  Paper,
  Grid,
  TextField,
  MenuItem,
  Button,
  Typography,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
} from '@mui/material';
import { observer } from 'mobx-react-lite';
import Form from 'mobx-react-form';
import { get } from 'lodash';

import { MONTHS } from 'shared/constants/dates';
import { EXPENSE_PERIOD_KEYS, EXPENSE_PERIOD_OPTIONS } from 'shared/constants/expenses';
import { expenseFormHelpers } from './operatingExpenseFormConfig';

import DetailedExpenseHistoryTable from './expense-history/DetailedTable';
import SummarizedExpenseHistoryTable from './expense-history/SummarizedTable';

import { EXPENSE_ENTRY_MODE_RADIO_BUTTON_ITEMS } from '../constants';
import { ExpenseHistory } from 'types/underwriting/income-approach/expenses';

export type YearColumn = { period: string; year: string; month: string };

type OperatingExpensesProps = {
  form: Form;
};

const OperatingExpenses = observer(({ form }: OperatingExpensesProps) => {
  const [period, setPeriod] = useState('');
  const [month, setMonth] = useState('');
  const [year, setYear] = useState('');

  const formValues = form.values();

  const expenseHistoryEntryModeField = form.$('incomeApproach.expenses.expenseHistoryEntryMode');
  const expenseHistory = get(formValues, 'incomeApproach.expenses.expenseHistory', []);

  const handlePeriodChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setPeriod(value);

    if (value === EXPENSE_PERIOD_KEYS.ACTUAL || value === EXPENSE_PERIOD_KEYS.PROJECTION) {
      setMonth('december');
    }
  }, []);

  const handleAddExpenseYear = useCallback(() => {
    const parsedYear = parseInt(year);

    if (parsedYear && period && month) {
      expenseFormHelpers.addExpenseYear(form, parsedYear, period, month);

      setPeriod('');
      setMonth('');
      setYear('');
    }
  }, [form, month, period, year]);

  const periodIsActualOrProjection = [EXPENSE_PERIOD_KEYS.ACTUAL, EXPENSE_PERIOD_KEYS.PROJECTION].includes(period);
  const isProjectionAlreadySelected = expenseHistory.some(
    (expense: ExpenseHistory) => expense.period === EXPENSE_PERIOD_KEYS.PROJECTION
  );

  const expenseHistoryOptions = isProjectionAlreadySelected
    ? EXPENSE_PERIOD_OPTIONS.filter((option) => option.value !== EXPENSE_PERIOD_KEYS.PROJECTION)
    : EXPENSE_PERIOD_OPTIONS;

  return (
    <Paper elevation={0} key="operating-expenses" sx={{ p: 2, mb: 6, minWidth: '1200px' }}>
      <Typography variant="h6">Operating Expenses</Typography>

      <FormControl>
        <RadioGroup
          {...form.$('incomeApproach.expenses.expenseHistoryEntryMode').bind()}
          row
          sx={{ minHeight: 40, mt: 0 }}
        >
          {EXPENSE_ENTRY_MODE_RADIO_BUTTON_ITEMS.map(({ value, label }) => (
            <FormControlLabel control={<Radio />} key={value} sx={{ mb: 3 / 4 }} {...{ label, value }} />
          ))}
        </RadioGroup>
      </FormControl>

      <Grid container spacing={2} sx={{ pt: 2, width: '100%' }} alignItems="center">
        <Grid item xs={12} sm={6} md={2.5}>
          <TextField
            select
            fullWidth
            label="Expense Period"
            value={period || ''}
            onChange={handlePeriodChange}
            variant="outlined"
            size="small"
          >
            {expenseHistoryOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid item xs={12} sm={6} md={2.5}>
          <TextField
            select
            fullWidth
            label="Month"
            value={month || ''}
            onChange={(event) => setMonth(event.target.value)}
            disabled={periodIsActualOrProjection}
            variant="outlined"
            size="small"
          >
            {MONTHS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid item xs={12} sm={6} md={2.5}>
          <TextField
            fullWidth
            label="Year"
            value={year || ''}
            onChange={(event) => setYear(event.target.value)}
            variant="outlined"
            size="small"
            type="text"
            placeholder="Enter year"
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4.5}>
          <Button
            variant="outlined"
            onClick={handleAddExpenseYear}
            size="small"
            sx={{
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              py: 1,
            }}
            disabled={!period || !month || !year}
          >
            Add Expense Year
          </Button>
        </Grid>
      </Grid>

      {expenseHistoryEntryModeField.value !== 'summarized' ? (
        <DetailedExpenseHistoryTable form={form} />
      ) : (
        <SummarizedExpenseHistoryTable form={form} />
      )}
    </Paper>
  );
});

export { OperatingExpenses };
